import {
  Component,
  ElementRef,
  EventEmitter,
  forwardRef,
  HostListener,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { NG_VALUE_ACCESSOR } from '@angular/forms';
import { take } from 'rxjs/operators';

import { ApiError, MessageService } from '../../../../core';
import { CondicaoPagamentoApi } from '../../../environment/condicoes-pagamentos/apis/condicao-pagamento.api';
import { EnumSituacaoCondicaoPagamento } from '../../../environment/condicoes-pagamentos/enums/situacao-condicao-pagamento.enum';
import { ICondicaoPagamentoFilter } from '../../../environment/condicoes-pagamentos/interfaces/condicao-pagamento-filter.interface';
import { ICondicaoPagamento } from '../../../environment/condicoes-pagamentos/interfaces/condicao-pagamento.interface';
import { CustomControlValueAccessor } from '../../utils/custom-control-value-accessor/custom-control-value-accessor';
import { calculateDropdownStyle } from '../../utils/dom-events/dropdown-position.util';

interface IGrupoMultiplo {
  multiplo: number;
  condicoes: ICondicaoPagamento[];
}

@Component({
  selector: 'sgt-novo-condicao-pagamento-select',
  templateUrl: './novo-condicao-pagamento-select.component.html',
  styleUrls: ['./novo-condicao-pagamento-select.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => NovoCondicaoPagamentoSelectComponent),
      multi: true,
    },
  ],
})
export class NovoCondicaoPagamentoSelectComponent
  extends CustomControlValueAccessor<number>
  implements OnChanges, OnDestroy {
  @Input() situacoes: EnumSituacaoCondicaoPagamento[] = [EnumSituacaoCondicaoPagamento.ATIVA];

  @Input() idCondicaoPagamentoPadrao: number | null = null;

  @Input() prazosMedios: number[] | null = null;

  @Output() condicaoPagamentoChange = new EventEmitter<ICondicaoPagamento | null>();

  @ViewChild('triggerRef') private triggerRef!: ElementRef<HTMLElement>;
  @ViewChild('searchInputRef') private searchInputRef!: ElementRef<HTMLInputElement>;
  @ViewChild('listRef') private listRef!: ElementRef<HTMLElement>;

  condicoes: ICondicaoPagamento[] = [];
  condicaoSelecionada: ICondicaoPagamento | null = null;

  searchTerm = '';

  isOpen = false;
  isLoading = false;
  indiceAtivo = -1;
  dropdownStyle: { [key: string]: string } = {};

  private carregado = false;

  constructor(
    private elementRef: ElementRef,
    private condicaoPagamentoApi: CondicaoPagamentoApi,
    private messageService: MessageService
  ) {
    super();
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: Event): void {
    if (this.isOpen && !this.elementRef.nativeElement.contains(event.target)) {
      this.fechar();
      this.markAsTouched();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    const mudouFiltro =
      changes.situacoes || changes.idCondicaoPagamentoPadrao || changes.prazosMedios;
    if (!mudouFiltro) {
      return;
    }
    const isFirstChange =
      changes.situacoes?.firstChange === true ||
      changes.idCondicaoPagamentoPadrao?.firstChange === true ||
      changes.prazosMedios?.firstChange === true;
    if (isFirstChange) {
      return;
    }
    const apenasPadraoJaCarregado =
      !changes.situacoes &&
      !changes.prazosMedios &&
      this.carregado &&
      (this.idCondicaoPagamentoPadrao == null ||
        this.condicoes.some((c) => c.id === this.idCondicaoPagamentoPadrao));
    if (apenasPadraoJaCarregado) {
      return;
    }
    this.carregado = false;
    this.condicoes = [];
    if (this.isOpen || this.value != null) {
      this.load();
    }
  }

  ngOnDestroy(): void {
    window.removeEventListener('scroll', this.scrollListener, true);
  }

  toggle(event: Event): void {
    event.stopPropagation();
    if (this.disabled) {
      return;
    }
    if (this.isOpen) {
      this.fechar();
    } else {
      this.abrir();
    }
  }

  selecionar(condicao: ICondicaoPagamento, event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    this.condicaoSelecionada = condicao;
    this.fechar();
    this.emitChange(condicao.id);
    this.condicaoPagamentoChange.emit(condicao);
    this.markAsTouched();
  }

  limpar(event: Event): void {
    event.stopPropagation();
    this.condicaoSelecionada = null;
    this.searchTerm = '';
    this.clearValue();
    this.condicaoPagamentoChange.emit(null);
    this.markAsTouched();
  }

  get condicoesFiltradas(): ICondicaoPagamento[] {
    return this.condicoes.filter((c) => this.matchesText(c, this.searchTerm));
  }

  get grupos(): IGrupoMultiplo[] {
    return this.agruparPorMultiplo(this.condicoesFiltradas);
  }

  get condicoesOrdenadas(): ICondicaoPagamento[] {
    return this.grupos.reduce<ICondicaoPagamento[]>(
      (acc, grupo) => acc.concat(grupo.condicoes),
      []
    );
  }

  isItemAtivo(condicao: ICondicaoPagamento): boolean {
    return this.condicoesOrdenadas[this.indiceAtivo]?.id === condicao.id;
  }

  onKeydown(event: KeyboardEvent): void {
    const lista = this.condicoesOrdenadas;
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        this.moverIndiceAtivo(1, lista.length);
        break;
      case 'ArrowUp':
        event.preventDefault();
        this.moverIndiceAtivo(-1, lista.length);
        break;
      case 'Enter':
        event.preventDefault();
        if (lista[this.indiceAtivo]) {
          this.selecionar(lista[this.indiceAtivo], event);
        }
        break;
      case 'Escape':
        event.preventDefault();
        this.fechar();
        break;
    }
  }

  multiploLabel(multiplo: number): string {
    return multiplo ? `Múltiplo de ${multiplo}` : 'Outras Condições';
  }

  getPrazos(condicao: ICondicaoPagamento): number[] {
    const match = condicao.nome.match(/\d+(?:\/\d+)+/);
    if (match) {
      return match[0].split('/').map(Number);
    }
    if (condicao.quantidadeParcelas <= 1) {
      return [condicao.prazoInicial];
    }
    const prazos: number[] = [];
    for (let i = 0; i < condicao.quantidadeParcelas; i++) {
      prazos.push(condicao.prazoInicial + i * condicao.multiplo);
    }
    return prazos;
  }

  isAtiva(condicao: ICondicaoPagamento): boolean {
    return condicao.situacao === EnumSituacaoCondicaoPagamento.ATIVA;
  }

  isCliente(condicao: ICondicaoPagamento): boolean {
    return condicao.situacao === EnumSituacaoCondicaoPagamento.CLIENTE;
  }

  isInativa(condicao: ICondicaoPagamento): boolean {
    return condicao.situacao === EnumSituacaoCondicaoPagamento.INATIVA;
  }

  protected updateValue(newValue: number | null): void {
    if (newValue == null) {
      this.condicaoSelecionada = null;
      return;
    }
    if (!this.condicoes.length) {
      if (!this.carregado) {
        this.load();
      }
      return;
    }
    this.condicaoSelecionada = this.condicoes.find((c) => c.id === newValue) ?? null;
  }

  protected onDisabledStateChange(): void {
    if (this.disabled) {
      this.fechar();
    }
  }

  private readonly scrollListener = (event: Event): void => {
    if (this.elementRef.nativeElement.contains(event.target)) {
      return;
    }
    this.fechar();
  };

  private abrir(): void {
    this.isOpen = true;
    this.searchTerm = '';
    this.adjustDropdownPosition();
    window.addEventListener('scroll', this.scrollListener, true);
    if (!this.carregado) {
      this.load();
    } else {
      this.sincronizarIndiceAtivo();
      this.scrollItemSelecionadoIntoView();
    }
    setTimeout(() => this.searchInputRef?.nativeElement.focus(), 0);
  }

  private sincronizarIndiceAtivo(): void {
    this.indiceAtivo = this.condicaoSelecionada
      ? this.condicoesOrdenadas.findIndex((c) => c.id === this.condicaoSelecionada?.id)
      : -1;
  }

  private scrollItemSelecionadoIntoView(): void {
    if (!this.condicaoSelecionada) {
      return;
    }
    setTimeout(() => {
      const item = this.listRef?.nativeElement.querySelector('[data-selected]');
      item?.scrollIntoView({ block: 'nearest' });
    }, 0);
  }

  private moverIndiceAtivo(delta: number, total: number): void {
    if (!total) {
      this.indiceAtivo = -1;
      return;
    }
    const base = this.indiceAtivo === -1 && delta < 0 ? total : this.indiceAtivo;
    this.indiceAtivo = (base + delta + total) % total;
    this.scrollIndiceAtivoIntoView();
  }

  private scrollIndiceAtivoIntoView(): void {
    setTimeout(() => {
      const item = this.listRef?.nativeElement.querySelector('[data-ativo]');
      item?.scrollIntoView({ block: 'nearest' });
    }, 0);
  }

  private fechar(): void {
    this.isOpen = false;
    this.searchTerm = '';
    this.indiceAtivo = -1;
    window.removeEventListener('scroll', this.scrollListener, true);
  }

  private adjustDropdownPosition(): void {
    const rect = this.triggerRef.nativeElement.getBoundingClientRect();
    this.dropdownStyle = calculateDropdownStyle(rect);
  }

  private load(): void {
    this.isLoading = true;
    this.condicaoPagamentoApi
      .fetchList(this.buildFilter())
      .pipe(take(1))
      .subscribe(
        (response) => {
          this.isLoading = false;
          this.carregado = true;
          this.condicoes = response?.data ?? [];
          if (this.value != null) {
            this.condicaoSelecionada = this.condicoes.find((c) => c.id === this.value) ?? null;
          }
          if (this.isOpen) {
            this.sincronizarIndiceAtivo();
            this.scrollItemSelecionadoIntoView();
          }
        },
        (error: ApiError) => {
          this.isLoading = false;
          this.messageService.error(
            error.message,
            'Erro ao pesquisar as condições de pagamento',
            error
          );
        }
      );
  }

  private buildFilter(): ICondicaoPagamentoFilter {
    const situacoes = this.situacoes?.length
      ? this.situacoes
      : [EnumSituacaoCondicaoPagamento.ATIVA];

    const filter: ICondicaoPagamentoFilter = { situacoes };

    if (this.idCondicaoPagamentoPadrao != null) {
      filter.idCondicaoPagamentoPadrao = this.idCondicaoPagamentoPadrao;
    }
    if (this.prazosMedios?.length) {
      filter.prazosMedios = this.prazosMedios;
    }
    return filter;
  }

  private matchesText(condicao: ICondicaoPagamento, termo: string): boolean {
    const q = termo.trim().toLowerCase();
    if (!q) {
      return true;
    }
    return (
      condicao.nome.toLowerCase().includes(q) ||
      (condicao.codigoIntegracaoErp ?? '').toLowerCase().includes(q) ||
      this.getPrazos(condicao).join('/').includes(q)
    );
  }

  private agruparPorMultiplo(lista: ICondicaoPagamento[]): IGrupoMultiplo[] {
    const multiplos = [...new Set(lista.map((c) => c.multiplo))].sort((a, b) => {
      if (!a) {
        return 1;
      }
      if (!b) {
        return -1;
      }
      return a - b;
    });
    return multiplos.map((multiplo) => ({
      multiplo,
      condicoes: lista.filter((c) => c.multiplo === multiplo),
    }));
  }
}
